import { LoadingSpinner, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack, ThemeProvider } from "@mui/material";
import { isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { requestPushPermissions } from "@src/appV2/NativeAdapters/pushNotifications.adapter";
import { ACCOUNTS_BASE_PATH } from "@src/appV2/redesign/Accounts/paths";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useUpdateWorkerCache } from "@src/appV2/Worker/useUpdateWorkerCache";
import { USER_EVENTS } from "@src/constants";
import { type ReactElement, useMemo, useRef } from "react";

import { PageWrapper } from "../components/PageWrapper";
import { StickyScrollPageHeader } from "../components/StickyScrollPageHeader";
import { PageContent } from "../WorkerShifts/components/PageContent";
import { useGetNotificationPreferences } from "./api/useGetNotifcationPreferences";
import { useSetNotificationPreferences } from "./api/useSetNotificationPreferences";
import {
  type NotificationPreferenceCapability,
  NotificationPreferenceItem,
} from "./components/NotificationPreferenceItem";
import { NotificationSettingsBottomSheet } from "./components/NotificationSettingsBottomSheet";
import { type PreferenceOnUpdateParams, ResponseCodes } from "./types";

const ACCOUNT_AND_UPDATES_PREFERENCE_KEY = "accountAndShiftsUpdates";

export function NotificationPreferencePage(): ReactElement {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const notificationSettingsBottomSheetModalState = useModalState();

  const updateWorkerCache = useUpdateWorkerCache();
  const worker = useDefinedWorker();

  const { showErrorToast } = useToast();

  const {
    data: notificationPreferencesData,
    refetch: notificationPreferencesRefetch,
    isLoading: isLoadingGet,
  } = useGetNotificationPreferences();
  const preferences = useMemo(() => {
    if (
      notificationPreferencesData &&
      typeof notificationPreferencesData.data !== "string" &&
      notificationPreferencesData.data
    ) {
      updateWorkerCache({
        notificationPreferences: notificationPreferencesData.data.notificationPreferences,
      });
      return notificationPreferencesData.data.notificationPreferences;
    }

    return undefined;
  }, [notificationPreferencesData, updateWorkerCache]);

  const { mutateAsync: setNotificationPreference, isLoading: isLoadingSet } =
    useSetNotificationPreferences();

  const isLoading = isLoadingGet || isLoadingSet;

  function logUpdate(newValue: PreferenceOnUpdateParams) {
    const { key, value } = newValue;

    const workerId = worker.userId;

    if (key === "off") {
      const offMeta = {
        makeEnabled: !value,
        workerId,
      };

      logEvent(USER_EVENTS.NOTIFICATION_PREFERENCE_CHANNEL_UPDATE, {
        channelName: "push",
        ...offMeta,
      });
      logEvent(USER_EVENTS.NOTIFICATION_PREFERENCE_CHANNEL_UPDATE, {
        channelName: "sms",
        ...offMeta,
      });

      return;
    }

    const meta = {
      makeEnabled: value,
      workerId,
    };

    if (["push", "sms"].includes(key)) {
      logEvent(USER_EVENTS.NOTIFICATION_PREFERENCE_CHANNEL_UPDATE, {
        channelName: key,
        ...meta,
      });
      return;
    }

    logEvent(USER_EVENTS.NOTIFICATION_PREFERENCE_UPDATE, {
      preferenceName: key,
      ...meta,
    });
  }

  /**
   * Updates preference and updates preference & agent store states
   * @param newValue of type PreferenceOnUpdateParams
   */
  const updatePreference = async (newValue: PreferenceOnUpdateParams) => {
    try {
      const response = await setNotificationPreference({
        notificationPreference: { ...newValue },
      });
      if (response.code === ResponseCodes.SUCCESS) {
        logUpdate(newValue);
        await notificationPreferencesRefetch();
      }
    } catch {
      showErrorToast("Could not change notifications, please try again later");
    }
  };

  const onUpdate = async (preferenceKey: string, capability: NotificationPreferenceCapability) => {
    if (capability.type === "push" && capability.enabled) {
      const { receive } = isCapacitorPlatform()
        ? await requestPushPermissions()
        : { receive: "granted" };

      if (receive === "denied") {
        notificationSettingsBottomSheetModalState.openModal();
        return;
      }
    }

    if (preferenceKey === ACCOUNT_AND_UPDATES_PREFERENCE_KEY) {
      // this preference key is FE only
      // currently, the keys "push", "sms", and "off" correspond to it
      // we're getting rid of "off" so we make sure to disable it, if any capability is enabled
      if (capability.enabled && preferences?.off) {
        void updatePreference({ key: "off", value: false });
      }

      void updatePreference({ key: capability.type, value: capability.enabled });
    } else {
      void updatePreference({ key: preferenceKey, value: capability.enabled });
    }
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          shouldWrapTitle
          title="Notification Preferences"
          scrollContainerRef={scrollContainerRef}
          headerVariant="tertiary"
          fallbackGoBackPath={ACCOUNTS_BASE_PATH}
        />
        <PageContent ref={scrollContainerRef}>
          {isLoading && !isDefined(preferences) && (
            <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
              <LoadingSpinner size="large" />
            </Box>
          )}
          {isDefined(preferences) && (
            <Stack spacing={5} sx={(theme) => ({ paddingTop: theme.spacing(5) })}>
              <NotificationPreferenceItem
                preferenceKey={ACCOUNT_AND_UPDATES_PREFERENCE_KEY}
                iconType="user-colored"
                title="Account and updates"
                description="Receive notifications about your account, scheduled shifts, payments, and more."
                capabilities={[
                  { type: "push", enabled: !preferences?.off && (preferences?.push ?? false) },
                  { type: "sms", enabled: !preferences?.off && (preferences?.sms ?? false) },
                ]}
                disabled={isLoading}
                onUpdate={onUpdate}
              />
              <NotificationPreferenceItem
                preferenceKey="urgentShiftNotification"
                iconType="time-flies"
                title="Urgent shift notifications"
                description="Be notified when high-earning Urgent Shifts become available to book."
                capabilities={[
                  { type: "push", enabled: preferences?.urgentShiftNotification?.isOn ?? false },
                ]}
                disabled={isLoading}
                onUpdate={onUpdate}
              />
              <NotificationPreferenceItem
                preferenceKey="priorityAccessPush"
                iconType="lock-with-star-violet"
                title="Priority access notifications"
                description="Receive updates when you get early access to shifts for your solid track record on the platform."
                capabilities={[{ type: "push", enabled: preferences?.priorityAccessPush ?? false }]}
                disabled={isLoading}
                onUpdate={onUpdate}
              />
              <NotificationPreferenceItem
                preferenceKey="placementsMarketingPush"
                iconType="briefcase"
                title="Clipboard hiring notifications"
                description="Receive notifications when new permanent job opportunities are posted in your area."
                capabilities={[
                  { type: "push", enabled: preferences?.placementsMarketingPush ?? false },
                ]}
                bottomDivider={false}
                disabled={isLoading}
                onUpdate={onUpdate}
              />
            </Stack>
          )}
        </PageContent>
      </PageWrapper>
      <NotificationSettingsBottomSheet modalState={notificationSettingsBottomSheetModalState} />
    </ThemeProvider>
  );
}
